import React from 'react';
import { useTranslation } from 'react-i18next';
import { categoryColors } from '../tokens';
import { mix } from '../themeMix';
import { useUiV2 } from '../../shell/uiFlags';

interface BadgeProps {
  label: string;
  variant?: 'category' | 'status' | 'semantic' | 'custom';
  color?: string;
  bg?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  label,
  variant = 'custom',
  color,
  bg,
  size = 'sm',
  className = '',
}) => {
  const { t } = useTranslation();
  const uiV2 = useUiV2();
  const getCategoryStyle = () => {
    const cat = categoryColors[label];
    if (cat) return { backgroundColor: cat.bg, color: cat.text };
    return { backgroundColor: mix.fillSoft, color: mix.textTertiary };
  };

  // New interface (ui_v2): a Blueprint tag. Neutral by default (gray fill,
  // 1 px border); a category is a classification, not a state, so it stays
  // neutral too. Only an explicit color/bg (callers encoding a state) tints it.
  const neutralV2 = { backgroundColor: 'var(--color-base)', color: 'var(--color-text-muted)', boxShadow: 'inset 0 0 0 1px var(--color-border)' };
  const getStyleV2 = () => {
    if (variant === 'category') return neutralV2;
    if (color || bg) return { backgroundColor: bg || 'var(--color-base)', color: color || 'var(--color-text-muted)' };
    return neutralV2;
  };

  const getStyle = () => {
    if (uiV2) return getStyleV2();
    if (variant === 'category') return getCategoryStyle();
    if (color || bg) return { backgroundColor: bg || mix.fillSoft, color: color || mix.textTertiary };
    return { backgroundColor: mix.fillSoft, color: mix.textTertiary };
  };

  const sizeStyles: React.CSSProperties = uiV2
    ? { fontSize: '11px', padding: '0 6px', height: size === 'sm' ? '18px' : '20px', lineHeight: size === 'sm' ? '18px' : '20px', boxSizing: 'border-box' }
    : size === 'sm'
      ? { fontSize: '11px', padding: '1px 6px' }
      : { fontSize: '12px', padding: '2px 8px' };

  const displayLabel = variant === 'category'
    ? t(`connectors.category.${label.toLowerCase()}`, label)
    : label;

  return (
    <span
      className={className}
      style={{
        ...getStyle(),
        borderRadius: '2px',
        fontWeight: 500,
        lineHeight: '18px',
        ...sizeStyles,
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        fontFamily: 'var(--font-interface)',
      }}
    >
      {displayLabel}
    </span>
  );
};

export default Badge;
